import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Phone, Edit, Trash2, User, CheckCircle, XCircle, Video, ExternalLink, Mail, Copy, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeEN } from "@/lib/dateUtils";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";

interface ScheduledCallCardProps {
  call: {
    id: string;
    subject?: string;
    title?: string;
    scheduled_for?: string;
    to_e164?: string;
    call_purpose?: string;
    call_agenda?: string;
    description?: string;
    status?: string;
    contact_id?: string | null;
    lead_id?: string | null;
    deal_id?: string | null;
    channel?: string;
    meeting_url?: string;
    attendees?: any;
    created_by?: string;
    rescheduled_count?: number;
    original_scheduled_for?: string;
    handler?: {
      display_name?: string;
      email?: string;
    };
  };
  contactName?: string;
  onStartCall?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onReschedule?: () => void;
}

export function ScheduledCallCard({
  call,
  contactName,
  onStartCall,
  onEdit,
  onDelete,
  onComplete,
  onCancel,
  onReschedule,
}: ScheduledCallCardProps) {
  const navigate = useNavigate();

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (call.lead_id) {
      navigate(createPageUrl("LeadDetail", { id: call.lead_id }));
    } else if (call.deal_id) {
      navigate(createPageUrl("DealDetail", { id: call.deal_id }));
    } else if (call.contact_id) {
      navigate(createPageUrl("ContactDetail", { id: call.contact_id }));
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">Agendada</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Concluída</Badge>;
      case 'canceled':
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">Cancelada</Badge>;
      case 'missed':
        return <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20">Perdida</Badge>;
      case 'rescheduled':
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">Remarcada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPurposeLabel = (purpose?: string) => {
    const labels: Record<string, string> = {
      'follow-up': 'Follow-up',
      'demo': 'Demo',
      'closing': 'Closing',
      'consultation': 'Consultation',
      'support': 'Support',
      'qualification': 'Qualification',
      'referral_request': 'Pedir Indicação',
      'other': 'Other',
    };
    return labels[purpose || ''] || purpose;
  };

  const isScheduled = call.status === 'scheduled';
  const isPast = call.scheduled_for && new Date(call.scheduled_for) < new Date();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-3 flex-1">
            {call.channel === 'video' ? (
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <Video className="w-5 h-5 text-purple-600" />
              </div>
            ) : (
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground">
                {call.title || 'Sem título'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {call.channel === 'video' ? 'Video Call' : 'Voice Call'}
              </p>
            {contactName && (
              <p className="text-sm text-muted-foreground mt-1">
                <User className="inline w-3 h-3 mr-1" />
                {contactName}
              </p>
            )}
            {call.to_e164 && (
              <button
                onClick={handlePhoneClick}
                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 hover:underline transition-colors cursor-pointer group"
                disabled={!call.lead_id && !call.contact_id && !call.deal_id}
              >
                <Phone className="w-3 h-3 group-hover:scale-110 transition-transform" />
                <span className="font-medium">{call.to_e164}</span>
              </button>
            )}
            </div>
          </div>
          {getStatusBadge(call.status)}
        </div>
      </CardHeader>

      <CardContent className="space-y-2 pb-3">
        {call.scheduled_for && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-foreground font-medium">
              {formatDateTimeEN(call.scheduled_for)}
            </span>
            {(call.rescheduled_count ?? 0) > 0 && (
              <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 text-xs">
                Remarcada {call.rescheduled_count}x
              </Badge>
            )}
          </div>
        )}

        {call.call_purpose && (
          <div className="text-sm">
            <span className="text-muted-foreground">Purpose: </span>
            <span className="text-foreground">{getPurposeLabel(call.call_purpose)}</span>
          </div>
        )}

        {call.handler?.display_name && (
          <div className="text-sm">
            <span className="text-muted-foreground">Owner: </span>
            <span className="text-foreground">{call.handler.display_name}</span>
          </div>
        )}

        {call.call_agenda && (
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
            {call.call_agenda}
          </p>
        )}

        {/* Video Call Specific Info */}
        {call.channel === 'video' && (
          <div className="space-y-2 pt-2 border-t mt-2">
            {call.meeting_url && (
              <div className="text-sm">
                <span className="text-muted-foreground">Meeting Link: </span>
                <div className="flex items-center gap-2">
                  <a
                    href={call.meeting_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1 inline-flex"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Join Meeting
                  </a>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(call.meeting_url!);
                      toast.success("Link copied to clipboard!");
                    }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
            
            {call.attendees && Array.isArray(call.attendees) && call.attendees.length > 0 && (
              <div className="text-sm">
                <span className="text-muted-foreground">Attendees: </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {call.attendees.map((email: string, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      <Mail className="w-3 h-3 mr-1" />
                      {email}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {call.description && (
              <div className="text-sm">
                <span className="text-muted-foreground">Description: </span>
                <p className="text-foreground mt-1 text-xs line-clamp-3">
                  {call.description}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex gap-2 pt-3 border-t">
        {isScheduled && (
          <>
            {!isPast && onStartCall && (
              <Button size="sm" onClick={onStartCall} className="flex-1">
                <Phone className="w-4 h-4 mr-1" />
                Start
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="outline" onClick={onEdit} title="Editar">
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {onReschedule && (
              <Button size="sm" variant="outline" onClick={onReschedule} className="text-amber-600 hover:text-amber-700" title="Remarcar">
                <CalendarClock className="w-4 h-4" />
              </Button>
            )}
            {onComplete && (
              <Button size="sm" variant="outline" onClick={onComplete} className="text-green-600 hover:text-green-700" title="Completar">
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
            {onCancel && (
              <Button size="sm" variant="outline" onClick={onCancel} className="text-orange-600 hover:text-orange-700" title="Cancelar">
                <XCircle className="w-4 h-4" />
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="ghost" onClick={onDelete} className="text-destructive hover:text-destructive" title="Excluir">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
        {!isScheduled && (
          <div className="flex-1 text-sm text-center text-muted-foreground">
            {call.status === 'completed' && 'Reunião realizada'}
            {call.status === 'canceled' && 'Reunião cancelada'}
            {call.status === 'missed' && 'Reunião perdida'}
            {call.status === 'rescheduled' && 'Reunião remarcada'}
            {!['completed', 'canceled', 'missed', 'rescheduled'].includes(call.status || '') && call.status}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
